/**
 * ScreenInfo
 * 画面情報の管理（スクロールバー幅、ヘッダー高さなどをCSS変数として設定）
 *
 * @usage
 * JS: new ScreenInfo();
 */
export default class ScreenInfo {
  constructor(options = {}) {
    this.options = {
      headerSelector: '.l-header',
      footerSelector: '.l-footer',
      updateDelay: 100, // リサイズ時の更新遅延
      ...options
    };

    this.header = document.querySelector(this.options.headerSelector);
    this.footer = document.querySelector(this.options.footerSelector);
    this.resizeTimer = null;

    this.init();
  }

  init() {
    this.updateScreenInfo();
    this.bindEvents();
  }

  bindEvents() {
    // リサイズ時の処理（デバウンス処理付き）
    window.addEventListener('resize', () => {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      
      this.resizeTimer = setTimeout(() => {
        this.updateScreenInfo();
      }, this.options.updateDelay);
    });

    // ロード時の処理
    window.addEventListener('load', () => {
      this.updateScreenInfo();
    });

    // オリエンテーション変更時の処理
    window.addEventListener('orientationchange', () => {
      // オリエンテーション変更は遅延が必要
      setTimeout(() => {
        this.updateScreenInfo();
      }, 300);
    });
  }

  updateScreenInfo() {
    const root = document.documentElement;

    // ビューポートサイズ
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // スクロールバー幅の計算
    const scrollbarWidth = this.calculateScrollbarWidth();

    // ヘッダー高さ
    const headerHeight = this.header ? this.header.offsetHeight : 0;

    // フッター高さ
    const footerHeight = this.footer ? this.footer.offsetHeight : 0;

    // デバイス情報
    const devicePixelRatio = window.devicePixelRatio || 1;

    // CSS変数として設定
    root.style.setProperty('--vw', `${vw}px`);
    root.style.setProperty('--vh', `${vh}px`);
    root.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
    root.style.setProperty('--header-height', `${headerHeight}px`);
    root.style.setProperty('--footer-height', `${footerHeight}px`);
    root.style.setProperty('--device-pixel-ratio', devicePixelRatio);

    // 計算値も提供
    root.style.setProperty('--content-height', `${vh - headerHeight}px`);
    root.style.setProperty('--main-height', `${vh - headerHeight - footerHeight}px`);
    root.style.setProperty('--vw-without-scrollbar', `${vw - scrollbarWidth}px`);

    // 1vw、1vhの実際の値（スクロールバーを考慮）
    root.style.setProperty('--1vw', `${(vw - scrollbarWidth) / 100}px`);
    root.style.setProperty('--1vh', `${vh / 100}px`);

    // カスタムイベントの発火
    window.dispatchEvent(new CustomEvent('screeninfo:updated', {
      detail: {
        vw,
        vh,
        scrollbarWidth,
        headerHeight,
        footerHeight,
        devicePixelRatio
      }
    }));
  }

  calculateScrollbarWidth() {
    // スクロールバー幅を動的に計算
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    outer.style.msOverflowStyle = 'scrollbar';
    outer.style.width = '100px';
    outer.style.height = '100px';

    document.body.appendChild(outer);

    const widthNoScroll = outer.offsetWidth;
    outer.style.overflow = 'scroll';

    const inner = document.createElement('div');
    inner.style.width = '100%';
    outer.appendChild(inner);

    const widthWithScroll = inner.offsetWidth;

    outer.parentNode.removeChild(outer);

    return widthNoScroll - widthWithScroll;
  }

  // 特定の値を取得するメソッド
  getViewportWidth() {
    return window.innerWidth;
  }

  getViewportHeight() {
    return window.innerHeight;
  }

  getHeaderHeight() {
    return this.header ? this.header.offsetHeight : 0;
  }

  getFooterHeight() {
    return this.footer ? this.footer.offsetHeight : 0;
  }

  getScrollbarWidth() {
    return this.calculateScrollbarWidth();
  }

  // デバイス判定
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  isTabletDevice() {
    return /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent);
  }

  isDesktopDevice() {
    return !this.isMobileDevice() && !this.isTabletDevice();
  }

  // ブレイクポイント判定
  isBreakpoint(breakpoint) {
    const breakpoints = {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1400
    };

    const width = window.innerWidth;
    const bp = breakpoints[breakpoint];

    if (bp !== undefined) {
      return width >= bp;
    }

    // 数値が直接指定された場合
    if (typeof breakpoint === 'number') {
      return width >= breakpoint;
    }

    return false;
  }

  destroy() {
    // リサイズタイマーのクリア
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }

    // CSS変数の削除
    const root = document.documentElement;
    const variables = [
      '--vw', '--vh', '--scrollbar-width', '--header-height', 
      '--footer-height', '--device-pixel-ratio', '--content-height',
      '--main-height', '--vw-without-scrollbar', '--1vw', '--1vh'
    ];

    variables.forEach(variable => {
      root.style.removeProperty(variable);
    });
  }
}